/**
 * Splits an `@`-mention query into an optional category scope and the inner
 * query. When the text before the first `:` matches a category keyword
 * (case-insensitive), the menu scopes into that category (`@repo:foo` -> the
 * Repositories provider searching `foo`). Otherwise there is no scope and the
 * whole query is a root-level search. Only the first `:` delimits the scope, so
 * inner queries may contain further colons.
 *
 * The query string is the single source of truth for scoping, so the visible
 * `@repo:` token and the active category can never desync.
 *
 * Lives in its own module (not `LaunchMentionMenu.tsx`) so the component file
 * only exports components — a requirement of React Fast Refresh.
 */
export function parseMentionScope<C extends { keyword?: string }>(
  query: string,
  categories: readonly C[],
): { scope: C | null; innerQuery: string } {
  const colon = query.indexOf(':');
  if (colon >= 0) {
    const keyword = query.slice(0, colon).trim().toLowerCase();
    if (keyword) {
      const scope = categories.find((c) => c.keyword?.toLowerCase() === keyword) ?? null;
      if (scope) return { scope, innerQuery: query.slice(colon + 1) };
    }
  }
  return { scope: null, innerQuery: query };
}
